import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
  Box, Button, InputBase,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import StyledDataGrid from "components/bento/StyledDataGrid";
import { BG_TINT, BORDER, RADIUS_INPUT, PURPLE_MAIN } from "components/bento/bentoTokens";
import { GetPackagesByCustomerId } from "api/packages";

// Lets staff browse & multi-select packages belonging to ONE customer, for
// the Receipt "one receipt = one customer" flow in CreateTab.jsx — replaces
// the old single scan-a-tracking-number flow.
const PackagePickerDialog = ({ open, customerId, excludePackageIds = [], onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);

  const refresh = (searchValue = search) => {
    if (!customerId) return;
    setIsLoading(true);
    GetPackagesByCustomerId(customerId, 0, 100, {}, searchValue).then((res) => {
      setRows(res.status === 200 && Array.isArray(res.data) ? res.data : []);
      setIsLoading(false);
    });
  };

  useEffect(() => {
    if (open) {
      setSelectionModel([]);
      setSearch("");
      refresh("");
    } else {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId]);

  const visibleRows = rows.filter((r) => !excludePackageIds.includes(r._id));

  const columns = [
    { field: "tracking", headerName: t("salesDocuments.columnTracking"), flex: 1 },
    { field: "description", headerName: t("salesDocuments.columnDescription"), flex: 1.5 },
    { field: "quantity", headerName: t("salesDocuments.columnQty"), flex: 0.5 },
    { field: "amount", headerName: t("salesDocuments.columnPrice"), flex: 0.7 },
    { field: "shippingFee", headerName: t("salesDocuments.columnShippingFee"), flex: 0.7 },
    { field: "deliveryFee", headerName: t("salesDocuments.columnDeliveryFee"), flex: 0.7 },
    { field: "status", headerName: t("salesDocuments.columnStatus"), flex: 0.7 },
  ];

  const handleConfirm = () => {
    const selected = visibleRows.filter((r) => selectionModel.includes(r._id));
    onConfirm(selected);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" color="secondary">{t("salesDocuments.pickPackagesTitle")}</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{
          display: "flex", alignItems: "center", height: "40px",
          background: BG_TINT, border: `1px solid ${BORDER}`, borderRadius: RADIUS_INPUT, px: "12px", mb: "14px",
        }}>
          <InputBase
            placeholder={t("salesDocuments.searchPackagesPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { refresh(); e.preventDefault(); } }}
            sx={{ flex: 1, fontSize: "0.9rem" }}
          />
          <IconButton size="small" onClick={() => refresh()}><SearchIcon fontSize="small" /></IconButton>
        </Box>

        {!isLoading && visibleRows.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center" }}>
            <Typography color="text.secondary">{t("salesDocuments.noPackagesFound")}</Typography>
          </Box>
        ) : (
          <Box height="50vh" width="100%">
            <StyledDataGrid
              loading={isLoading}
              getRowId={(row) => row._id}
              rows={visibleRows}
              columns={columns}
              checkboxSelection
              selectionModel={selectionModel}
              onSelectionModelChange={(model) => setSelectionModel(model)}
              containerSx={{ height: "100%" }}
              autoHeight={false}
              sx={{
                "& .MuiCheckbox-root.Mui-checked, & .MuiCheckbox-root.MuiCheckbox-indeterminate": {
                  color: PURPLE_MAIN,
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <Button variant="outlined" color="secondary" onClick={onClose}>{t("common.cancel")}</Button>
        <Button variant="contained" color="secondary" disabled={selectionModel.length === 0} onClick={handleConfirm}>
          {t("salesDocuments.addSelectedPackages", { count: selectionModel.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PackagePickerDialog;
